/**
 * One-shot retry for the first federated (Google) sign-in.
 *
 * Cognito's PreSignUp account-linking makes the VERY FIRST federated sign-in for
 * an email that links to an existing account fail once: the link is created
 * during that failed attempt, and the immediate retry then succeeds. Rather than
 * show the user an error that fixes itself, {@code signInWithGoogle} arms a
 * marker before leaving for Google and the return handler retries once,
 * transparently.
 *
 * The decision is a pure function ({@link decideFederatedRetry}) so it can be
 * tested without a browser; the sessionStorage helpers keep the marker across
 * the redirect round-trip (per-tab, so parallel sign-ins don't share a counter).
 */
export const FED_PROVIDER_KEY = "a2a.federatedProvider";
export const FED_ATTEMPT_KEY = "a2a.federatedAttempt";

export function ssGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
export function ssSet(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* private-mode: the retry just won't fire */
  }
}
export function clearFederatedRetry(): void {
  try {
    window.sessionStorage.removeItem(FED_PROVIDER_KEY);
    window.sessionStorage.removeItem(FED_ATTEMPT_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * What the return handler should do given the current federated state.
 *
 * <ul>
 *   <li>{@code "wait"} — nothing armed, or the attempt is still in flight (no
 *       error and not yet authenticated): do nothing.</li>
 *   <li>{@code "clear"} — disarm the marker. Reached on success, on a
 *       declined-consent / not-a-test-user error (NOT the link quirk — don't
 *       bounce the user back to Google unasked), or once the single retry is
 *       spent (a genuine failure: let the error surface).</li>
 *   <li>{@code "retry"} — re-issue the federated redirect once.</li>
 * </ul>
 */
export function decideFederatedRetry(params: {
  provider: string | null;
  isAuthenticated: boolean;
  hasError: boolean;
  errorDetail: string;
  attempts: number;
}): "wait" | "clear" | "retry" {
  const { provider, isAuthenticated, hasError, errorDetail, attempts } = params;
  if (!provider) return "wait";
  if (isAuthenticated) return "clear";
  if (!hasError) return "wait";
  if (errorDetail.toLowerCase().includes("access_denied")) return "clear";
  if (attempts >= 1) return "clear";
  return "retry";
}
